import dayjs from "dayjs";
import { toast, ToastPromiseParams } from "react-toastify";

import { nextCargoApi } from "../../baseApi";
import { truckApi } from "../truck/truck.api";
import { Driver, ListDriverByCompany, SaveDriver } from "./driver.types";

export const driverApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    getDriver: build.query<Driver, Pick<Driver, "id">, Driver>({
      query: ({ id }) => `/driver/${id}`,
      transformResponse: (response) => ({
        ...response,
        birthDate: dayjs(response.birthDate).format("DD/MM/YYYY")
      })
    }),
    listDriversByCompany: build.infiniteQuery<
      ListDriverByCompany,
      { id?: number; params?: App.Filters.Base<Driver> },
      App.Filters.Pagination<Driver, { companyId?: number }>,
      ListDriverByCompany
    >({
      infiniteQueryOptions: {
        initialPageParam: { page: 0, pageSize: 20, sortDirection: "asc", sortField: "name" },
        getNextPageParam: (lastPage, _, lastPageParam) => {
          const nextPage = lastPageParam.page + 1;
          const remainingPages = lastPage?.total - nextPage;

          if (remainingPages <= 0) {
            return undefined;
          }

          return {
            ...lastPageParam,
            page: nextPage
          };
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
          const prevPage = firstPageParam.page - 1;
          if (prevPage < 0) return undefined;

          return {
            ...firstPageParam,
            page: prevPage
          };
        }
      },
      query: ({ queryArg: { params }, pageParam }) => ({
        url: "/driver",
        params: Object.assign({}, pageParam, params)
      }),
      transformResponse: (response) => {
        const transformedData = response.data.map((driver) => ({
          ...driver,
          birthDate: dayjs(driver.birthDate, "YYYY-MM-DD").format("DD/MM/YYYY")
        }));

        return {
          ...response,
          data: transformedData
        };
      },
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          pending: "Carregando motoristas...",
          error: "Erro ao carregar motoristas!"
        });
      }
    }),
    saveDriver: build.mutation<SaveDriver["result"], SaveDriver["args"]>({
      query: ({ company, shippingCompany, truck: _, id: __, ...body }) => {
        return {
          url: "/driver/save",
          method: "POST",
          body: {
            ...body,
            id: null,
            company: company?.id
              ? {
                  id: Number(company?.id)
                }
              : undefined,
            shippingCompany: shippingCompany?.id
              ? {
                  id: Number(shippingCompany?.id)
                }
              : undefined
          }
        };
      },
      onQueryStarted: async ({ id, truck }, { dispatch, queryFulfilled }) => {
        const isEdit = !!id;
        const textMap = new Map<boolean, ToastPromiseParams>([
          [true, { pending: "Editando motorista...", success: "Motorista editado com sucesso!", error: "Erro ao editar motorista!" }],
          [false, { pending: "Cadastrando motorista...", success: "Motorista cadastrado com sucesso!", error: "Erro ao cadastrar motorista!" }]
        ]);

        toast.promise(queryFulfilled, {
          pending: textMap.get(isEdit)?.pending,
          success: textMap.get(isEdit)?.success,
          error: textMap.get(isEdit)?.error
        });

        try {
          const { data } = await queryFulfilled;
          const truckData = truck?.[0];

          const { data: hasTruckData, isSuccess } = await dispatch(truckApi.endpoints.getHasTruck.initiate(truckData?.licensePlate || ""));
          if (!isSuccess) return;

          if (!hasTruckData?.exists) {
            //@ts-expect-error - Erro que não impacta a lógica, apenas uma questão de tipagem chata
            await dispatch(truckApi.endpoints.saveNewTruck.initiate({ driver: { id: Number(data.id) }, ...truckData }));
            return;
          }

          //@ts-expect-error - Erro que não impacta a lógica, apenas uma questão de tipagem chata
          await dispatch(truckApi.endpoints.saveRebindTruck.initiate({ driver: { id: Number(data.id) }, truck: truckData }));
        } catch {
          // Ignore catch
        }
      }
    })
  })
});

driverApi.enhanceEndpoints({
  endpoints: {
    getDriver: {
      providesTags: (result, err, { id }) => [{ type: "Driver", id }]
    },
    listDriversByCompany: {
      providesTags: (response) =>
        response
          ? [...response.pages.flatMap((page) => page.data.map(({ id }) => ({ type: "Driver" as const, id }))), { type: "Driver", id: "LIST" }]
          : [{ type: "Driver", id: "LIST" }]
    },
    saveDriver: {
      invalidatesTags: (result, err, { id }) => (!err ? [{ type: "Driver", id: id ?? "LIST" }] : [])
    }
  }
});
